<template>
  <LoadingSpinner v-if="loading" />
  <Form v-else @submit="submit" :title="$t('meta.baseData')" :bodyClass="''">
    <FormInput :name="$t('zones.name')" v-model="zone.name" />

    <div class="form-wrap" v-if="costCenters.length > 0">
      <label>{{ $t('meta.costCenter') }}</label>
      <v-select
        class="form-select__select"
        v-model="zone.cost_center_uuid"
        :options="costCenterOptions"
        :disabled="costCenterSelectDisabled"
        :reduce="costCenter => costCenter.uuid"
        :searchable="true"
        :filterable="true"
      >
      </v-select>
    </div>

    <FormInput :name="$t('areas.description')" v-model="zone.description" labelOptional />

    <FormSection :name="$t('meta.zoneIdentifier')">
      <div class="row row-gutter-20">
        <div class="col-8">
          <FormInput v-model="identifier.name" :name="$t('meta.name')" />
        </div>

        <div class="col-8">
          <FormInput v-model="identifier.value" :name="$t('identifiers.value')" />
        </div>

        <div v-if="displayDeleteIdentifierButton" class="col-8" style="margin-top: 28px">
          <button type="button" class="button button-red button-small button-round" @click="deleteIdentifier">
            <span class="material-icons">delete</span>
          </button>
        </div>
      </div>
    </FormSection>

    <BaseMap
      :allowFourNewCoordinatesOnly="isNew"
      :editablePolygonFeature="geojsonFeature"
      :fitBounds="areaPolygon.properties.bbox"
      :staticPolygons="staticPolygonsForMap"
      :parentPolygon="areaPolygon"
      :enableDraw="isNew"
      @coordinates-updated="handleCoordinatesUpdate"
      @coordinates-reset="resetCoordinates"
      enableGeocoder
    />
    <Button type="submit">
      {{ $t('areas.update') }}
    </Button>
  </Form>
</template>

<script>
import Button from '@/components/Button/index.vue'
import FormInput from '@/components/Form/Input/index.vue'
import Form from '@/components/Form/index.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import BaseMap from '@/components/Map/base'

import costCentersApi from '@/api/costCenters'
import industryZoneIdentifiersApi from '@/api/industry/zones/identifiers'
import zonesApi from '@/api/zones'
import { staticPolygonsFromObject } from '@/utils/geojson'
import mapUtils from '@/utils/mapbox'

export default {
  inject: ['companyUser'],
  data() {
    let zone

    if (this.initialZone) {
      zone = { ...this.initialZone }
    } else {
      zone = { company_uuid: this.companyUser.company_uuid }
    }

    return {
      loading: true,
      zone: zone,
      identifier: zone.identifier || { name: '', value: '' },
      otherZones: [],
      costCenters: [],
    }
  },
  props: {
    initialZone: { type: Object },
    area: { type: Object, required: true },
    site: { type: Object, required: true },
  },
  watch: {
    initialZone(zone) {
      this.zone = { ...zone }
      this.identifier = this.zone.identifier || { name: '', value: '' }
    },
  },
  components: {
    Form,
    FormInput,
    LoadingSpinner,
    BaseMap,
    Button,
  },
  methods: {
    handleCoordinatesUpdate(newCoordinates) {
      const center = mapUtils.calculateCenterOfCoordinates(mapUtils.coordinatePairArrayToLngLatObject(newCoordinates))
      this.zone.mb_feature_polygon = JSON.stringify({
        type: 'FeatureCollection',
        features: [
          {
            id: this.zone.uuid,
            type: 'Feature',
            propertiers: {},
            geometry: {
              coordinates: [newCoordinates],
              type: 'Polygon',
            },
          },
        ],
      })

      this.zone.lat = center.lat
      this.zone.lng = center.lng
    },
    resetCoordinates() {
      this.zone.lat = null
      this.zone.lng = null
    },
    async deleteIdentifier() {
      if (this.identifier.uuid) {
        await industryZoneIdentifiersApi.delete(this.zone.uuid)
      }

      this.identifier = { name: '', value: '' }
    },
    submit() {
      const params = { ...this.zone }

      if (this.identifier.name.length > 0 || this.identifier.value.length > 0) {
        params.identifier_attributes = {
          name: this.identifier.name,
          value: this.identifier.value,
        }
      }

      this.$emit('submit', params)
    },
  },
  computed: {
    isNew() {
      return !this.zone.uuid
    },
    staticPolygonsForMap() {
      return [
        staticPolygonsFromObject('site', this.site),
        this.areaPolygon,
        ...this.otherZones.map(zone => staticPolygonsFromObject('zone', zone)),
      ]
    },
    areaPolygon() {
      return staticPolygonsFromObject('area', this.area)
    },
    geojsonFeature() {
      if (this.isNew) return null
      return JSON.parse(this.zone.mb_feature_polygon).features[0]
    },
    costCenterOptions() {
      return this.costCenters.map(function (costCenter) {
        return { label: costCenter.name, uuid: costCenter.uuid }
      })
    },
    costCenterSelectDisabled() {
      if (this.companyUser.user.admin) return false
      if (this.companyUser.roles.includes('company_admin')) return false

      return true
    },
    displayDeleteIdentifierButton() {
      if (this.identifier.uuid) {
        return true
      } else if (this.identifier.name.length > 0) {
        return true
      } else if (this.identifier.value.length > 0) {
        return true
      } else {
        return false
      }
    },
  },
  async mounted() {
    this.otherZones = (await zonesApi.getAllInSite(this.$route.params.site_uuid)).filter(
      zone => zone.uuid !== this.$route.params.zone_uuid,
    )

    this.costCenters = await costCentersApi.getPaginated({ company_uuid: this.companyUser.company_uuid, active: true })

    this.loading = false
  },
}
</script>
